import Header from '../components/Header'
import Footer from '../components/Footer'
import { Link } from 'react-router-dom'

const SaasDevelopment = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative bg-gray-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-30"
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=2850&q=80"
              alt="SaaS development"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
                <span className="block">Custom SaaS Development</span>
                <span className="block text-indigo-400">That Scales With Your Business</span>
              </h1>
              <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-300">
                We build secure, scalable, and user-friendly SaaS applications that solve real business problems and drive recurring revenue.
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-block bg-indigo-600 py-3 px-8 border border-transparent rounded-md text-base font-medium text-white hover:bg-indigo-700"
                >
                  Get Your Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { name: 'Faster Time-to-Market', value: '40-60%' },
                { name: 'Reduced Development Costs', value: '30-50%' },
                { name: 'Higher Customer Retention', value: '3-5x' },
                { name: 'Increased Operational Efficiency', value: '70%+' },
              ].map((stat) => (
                <div key={stat.name} className="text-center p-6 bg-gray-50 rounded-lg shadow">
                  <p className="text-3xl font-bold text-indigo-600">{stat.value}</p>
                  <p className="mt-2 text-sm font-medium text-gray-500">{stat.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Approach Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Our Approach</h2>
              <p className="mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
                Building SaaS Products That Users Love
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                We combine technical excellence with user-centric design to create successful SaaS solutions.
              </p>
            </div>

            <div className="mt-16">
              <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
                {[
                  {
                    name: 'Discovery & Validation',
                    description: 'We start by understanding your business goals and validating the market need',
                    features: ['Market research', 'Competitive analysis', 'User interviews', 'MVP definition']
                  },
                  {
                    name: 'UX/UI Design',
                    description: 'Creating intuitive interfaces that drive engagement and retention',
                    features: ['User flows', 'Wireframing', 'Prototyping', 'Design systems']
                  },
                  {
                    name: 'Agile Development',
                    description: 'Iterative development with continuous feedback loops',
                    features: ['Sprint planning', 'CI/CD pipelines', 'Code reviews', 'Automated testing']
                  },
                  {
                    name: 'Cloud Architecture',
                    description: 'Scalable infrastructure built for growth',
                    features: ['Microservices', 'Serverless', 'Containers', 'Load balancing']
                  },
                  {
                    name: 'Security & Compliance',
                    description: 'Enterprise-grade security from day one',
                    features: ['Data encryption', 'Role-based access', 'SOC 2 compliance', 'Penetration testing']
                  },
                  {
                    name: 'Analytics & Optimization',
                    description: 'Data-driven decisions for continuous improvement',
                    features: ['Usage analytics', 'Feature adoption', 'A/B testing', 'Performance monitoring']
                  }
                ].map((service) => (
                  <div key={service.name} className="relative bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-medium text-gray-900">
                      {service.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      {service.description}
                    </p>
                    <ul className="mt-4 space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="flex-shrink-0 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tech Stack Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase">Our Technology Stack</h2>
              <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Modern Tools for Building Scalable SaaS
              </p>
            </div>

            <div className="mt-12">
              <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
                {[
                  { name: 'Frontend', tech: ['React', 'Vue.js', 'TypeScript'] },
                  { name: 'Backend', tech: ['Node.js', 'Python', '.NET Core'] },
                  { name: 'Mobile', tech: ['React Native', 'Flutter', 'Swift', 'Kotlin'] },
                  { name: 'Database', tech: ['PostgreSQL', 'MongoDB', 'Firebase', 'Redis'] },
                  { name: 'DevOps', tech: ['AWS', 'Docker', 'Kubernetes', 'Terraform'] },
                  { name: 'AI/ML', tech: ['TensorFlow', 'PyTorch', 'NLP', 'Computer Vision'] },
                  { name: 'APIs', tech: ['GraphQL', 'REST', 'gRPC', 'WebSockets'] },
                  { name: 'Analytics', tech: ['Segment', 'Mixpanel', 'Amplitude', 'Looker'] },
                ].map((category) => (
                  <div key={category.name} className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{category.name}</h3>
                    <ul className="space-y-3">
                      {category.tech.map((item) => (
                        <li key={item} className="flex items-center">
                          <svg className="flex-shrink-0 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="ml-2 text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Case Study Section */}
        <div className="bg-indigo-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              <div className="mb-8 lg:mb-0">
                <h2 className="text-3xl font-extrabold text-white">
                  <span className="block">From Concept to</span>
                  <span className="block">10,000+ Users</span>
                </h2>
                <p className="mt-4 text-lg text-indigo-200">
                  How we helped a HR tech startup build a scalable SaaS platform that now serves enterprise clients worldwide.
                </p>
                <div className="mt-8">
                  <Link to="/case-studies" className="inline-flex items-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50">
                    Read Case Study
                  </Link>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-12 w-12 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <blockquote>
                      <p className="text-lg text-gray-700">
                        "The team delivered a flawless SaaS product that exceeded our expectations. Their expertise in cloud architecture allowed us to scale seamlessly as our user base grew 10x in the first year."
                      </p>
                      <footer className="mt-4">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="text-base font-medium text-gray-900">Michael Chen</div>
                            <div className="text-base font-medium text-indigo-600">CEO, TalentFlow</div>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div id="contact" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Ready to Build Your SaaS Product?
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Let's discuss how we can turn your vision into a successful software business.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Start Your Project
                </Link>
              </div>
              <div className="ml-3 inline-flex">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                >
                  See Our Work
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default SaasDevelopment
